import { Check, X } from 'heroicons-react'
import moment from 'moment'
import { useEffect, useMemo, useState } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import { CiWarning } from 'react-icons/ci'
import { toast } from 'react-toastify'

import { PlusIcon } from '@heroicons/react/24/outline'

import { cn } from '@/lib/utils'

import useServerEcoCycle from '../../../../../api/useServerEconomicCycle'
import useServer from '../../../../../api/useServerMain'
import useServerOrders from '../../../../../api/useServerOrders'
import AsyncComboBox from '../../../../../components/forms/AsyncCombobox'
import ComboBox from '../../../../../components/forms/Combobox'
import CurrencyAmountInput from '../../../../../components/forms/CurrencyAmountInput'
import InlineRadio from '../../../../../components/forms/InlineRadio'
import Input from '../../../../../components/forms/Input'
import Button from '../../../../../components/misc/Button'
import Fetching from '../../../../../components/misc/Fetching'
import Modal from '../../../../../components/misc/GenericModal'
import GenericTable, {
  DataTableInterface,
} from '../../../../../components/misc/GenericTable'
import {
  ApplyCouponBody,
  SelectInterface,
} from '../../../../../interfaces/InterfacesLocal'
import {
  OrderInterface,
  PriceInvoiceInterface,
  SimplePrice,
} from '../../../../../interfaces/ServerInterfaces'
import BillingReportPdf from '../../../../../reports/BillingReportPDF'
import { useAppSelector } from '../../../../../store/hooks'
import {
  cleanObj,
  formatCurrency,
  mathOperation,
  printPdf,
  truncateValue,
} from '../../../../../utils/helpers'
import { translatePaymetMethods } from '../../../../../utils/translate'
import { AddPay } from './AddPay'
import { EditPay } from './EditPay'
import PrepaidList from './PrepaidList'

export interface PrepaidReduced {
  paymentId: number
  amount: number
  codeCurrency: string
  payment: number
}

// New interfaces to replace any types
interface RegisteredPayment {
  amount: number
  codeCurrency: string
  paymentWay: string
  observations?: string
  madeById?: number
  operationNumber?: string
}

interface PaymentTableData {
  rowId: number
  payload: {
    'No.': number
    Fecha: string
    Monto: string
    Método: string
    amount: number
    codeCurrency: string
    paymentWay: string
    observations?: string
    id?: number
    madeById?: number
    operationNumber?: string
    registeredPaymentsNumber?: number
  }
}

interface PaymentInterface {
  closeModal: () => void
  dependencies: {
    order: OrderInterface | undefined | null
    updateSingleOrderState: (_order: OrderInterface) => void
    updateAllOrdersState: (_order: OrderInterface) => void
    deletePartialPayment: (_id: number) => void
    isFetching: boolean | undefined
    updateStateExternal?: (_isPartialPay: boolean) => void
  }
  origin?: string
}

const PaymentContainerV2 = ({ closeModal, dependencies }: PaymentInterface) => {
  const { control, getValues, trigger, watch, unregister, setValue } = useForm({
    mode: 'onChange',
  })

  const { remove, fields } = useFieldArray({
    name: 'registeredPayments',
    control,
  })
  const { business } = useAppSelector((state) => state.init)
  const {
    order,
    updateSingleOrderState,
    updateAllOrdersState,
    deletePartialPayment,
    isFetching: isFetchingDeleted,
    updateStateExternal,
  } = dependencies

  const { registerOrderPaymentV2, isFetching } = useServerEcoCycle()
  const { calculatePaymentDiff } = useServer()
  const { areas } = useAppSelector((state) => state.nomenclator)
  const [addPay, setAddPay] = useState(false)
  const [selectPay, setSelectPay] = useState<
    PaymentTableData['payload'] | null
  >(null)
  //Estados buttons
  const [shippingPriceShow, setShippingPriceShow] = useState(false)
  const [showDiscount, setShowDiscount] = useState(false)
  const [showCommission, setShowCommission] = useState(false)
  const [showCupon, setShowCupon] = useState(false)
  const [houseCosted, setHouseCosted] = useState(false)
  const [loadingHelper, setLoadingHelper] = useState(false)

  //Config Keys
  const allow_commission =
    business?.configurationsKey.find((item) => item.key === 'allow_commission')
      ?.value === 'true'
  const allow_discounts =
    business?.configurationsKey.find((item) => item.key === 'allow_discounts')
      ?.value === 'true'
  const show_coupons_in_tablet =
    business?.configurationsKey.find(
      (item) => item.key === 'show_coupons_in_tablet'
    )?.value === 'true'

  const {
    applyCoupon,
    couponResult,
    isFetching: fetchingCoupon,
    nullCoupon,
  } = useServerOrders()
  const [shippingPrice, setShippingPrice] = useState(
    order?.shippingPrice ?? { amount: 0, codeCurrency: 'CUP' }
  )
  useEffect(() => {
    if (/*origin === "online_shop" && */ order?.shippingPrice) {
      setShippingPriceShow(true)
      setValue('shippingPrice', order?.shippingPrice)
    }
  }, [order?.shippingPrice, setValue])

  useEffect(() => {
    setShippingPrice(getValues('shippingPrice'))
  }, [getValues])

  useEffect(() => {
    if (houseCosted) {
      unregister(['commission', 'discount', 'coupon'])
    }
    setValue('houseCosted', houseCosted)
  }, [houseCosted, setValue, unregister])

  useEffect(() => {
    if (!shippingPriceShow) {
      unregister('shippingPrice')
    }
    if (!showDiscount) {
      unregister('discount')
    }
    if (!showCommission) {
      unregister('commission')
    }
    if (showCommission || showDiscount) {
      unregister('coupon')
    }
  }, [shippingPriceShow, showDiscount, showCommission, unregister])

  //----------------------------------Loadings---------------------------------

  //Selectors ----------------------------------------------------------
  const salesAreas: SelectInterface[] = areas
    .filter((area) => area.type === 'SALE')
    .map((item) => ({ id: item.id, name: item.name }))

  const areaSelect = watch('areaId') ?? order?.areaSales

  const currentArea = areas.find((item) => item.id === areaSelect)

  const currenciesSelector =
    business?.availableCurrencies.map((itm) => itm?.code) ?? []

  const updateOrderState = (order: OrderInterface) => {
    if (updateSingleOrderState) {
      updateSingleOrderState(order)
    }
    if (updateAllOrdersState) {
      updateAllOrdersState(order)
    }
    remove()
    closeModal()
  }
  //Selectors ----------------------------------------------------------

  //Submit ----------------------------------------------------------------
  const submitAction = async (functionHelper?: () => void) => {
    if (await trigger()) {
      let data = getValues()
      if (negativeDiff) {
        data.isPartialPay = true
      }
      if (!!data.prepaidPayment && data.prepaidPayment.length !== 0) {
        data.prepaidPaymentIds = data.prepaidPayment.map(
          (item: PrepaidReduced) => item.paymentId
        )
      }
      data.coupons = []
      if (data.coupon) {
        data?.coupons.push(data.coupon)
      }
      delete data.prepaidPayment
      data.discount = data?.discount ?? 0
      data.commission = data?.commission ?? 0
      data = cleanObj(data)

      if (currentArea && !currentArea.giveChangeWith) {
        data.amountReturned = [...difference][0]
      }
      if (functionHelper) {
        setLoadingHelper(true)
      }
      registerOrderPaymentV2(order?.id ?? 0, data, (resp: OrderInterface) => {
        updateOrderState(resp)
        if (functionHelper) {
          functionHelper()
        }
        if (updateStateExternal) {
          updateStateExternal(data.isPartialPay)
        }
      })
    }
  }
  //----------------------------------------------------------------------

  //-----------------Helpers-------------------------------------------
  const paymentField = useMemo(() => {
    return (
      watch('registeredPayments')?.map((item: RegisteredPayment) => ({
        amount: item?.amount ?? 0,
        codeCurrency: item?.codeCurrency,
      })) ?? []
    )
  }, [watch])

  const prepaidAmounts: PriceInvoiceInterface[] =
    watch('prepaidPayment')?.map((item: PrepaidReduced) => ({
      amount: item.amount,
      codeCurrency: item.codeCurrency,
    })) ?? []
  //-----------------Helpers-------------------------------------------

  const acuPayment = useMemo(() => {
    let acuPayment: SimplePrice[] = []

    paymentField.forEach((item: { amount: number; codeCurrency: string }) => {
      const found = acuPayment.find(
        (itm) => itm.codeCurrency === item.codeCurrency
      )

      if (found) {
        acuPayment = acuPayment.map((itm) => {
          if (itm.codeCurrency === item.codeCurrency) {
            return {
              ...itm,
              amount: mathOperation(itm.amount, item.amount, 'addition', 2),
            }
          }

          return itm
        })
      } else {
        acuPayment.push({
          amount: item.amount,
          codeCurrency: item.codeCurrency,
        })
      }
    })

    return acuPayment
  }, [paymentField])

  //------------------Calculo de la orden------------
  const {
    toReturn: totalWithDiscounts,
    discountedTotal,
    commissionTotal,
    orderModifiers,
    aux,
  } = useMemo(() => {
    const toReturn: PriceInvoiceInterface[] = []
    const discountedTotal: PriceInvoiceInterface[] = []
    const commissionTotal: PriceInvoiceInterface[] = []

    const discount = watch('discount') ?? order?.discount
    const commission = watch('commission') ?? order?.commission
    const shippingPrice = watch('shippingPrice') ?? order?.shippingPrice
    let simplePrices: SimplePrice[] = []
    let aux: SimplePrice[] = []
    let totalPrices: SimplePrice[] = []

    const orderModifiers: {
      showName: string
      amount: number
      codeCurrency: string
    }[] = []

    for (const selledProduct of order?.selledProducts || []) {
      const found = simplePrices.find(
        (item) => item.codeCurrency === selledProduct.priceUnitary.codeCurrency
      )

      let priceTotal = mathOperation(
        selledProduct.priceUnitary.amount,
        selledProduct.quantity,
        'multiplication',
        3
      )

      priceTotal = truncateValue(priceTotal, 2)
      if (found) {
        simplePrices = simplePrices.map((item) => {
          if (
            item?.codeCurrency === selledProduct?.priceUnitary?.codeCurrency
          ) {
            return {
              ...item,
              amount: mathOperation(item?.amount, priceTotal, 'addition', 2),
            }
          }
          return item
        })
        aux = aux.map((item) => {
          if (
            item?.codeCurrency === selledProduct?.priceUnitary?.codeCurrency
          ) {
            return {
              ...item,
              amount: mathOperation(item?.amount, priceTotal, 'addition', 2),
            }
          }
          return item
        })
      } else {
        simplePrices.push({
          amount: priceTotal,
          codeCurrency: selledProduct.priceUnitary?.codeCurrency,
        })
        aux.push({
          amount: priceTotal,
          codeCurrency: selledProduct.priceUnitary?.codeCurrency,
        })
      }
    }

    totalPrices = [...simplePrices]

    if (discount || commission) {
      //Total to discount and comision
      let discountOperation = 0
      let commissionOperation = 0

      // var helper
      let auxDiscount = 0
      let auxCommission = 0

      for (const price of totalPrices) {
        if (discount) {
          //calculate value before discount
          discountOperation = mathOperation(
            price.amount,
            mathOperation(price.amount, discount, 'multiplication') / 100,
            'subtraction',
            2
          )
          discountedTotal.push({
            amount: (price.amount * discount) / 100,
            codeCurrency: price.codeCurrency,
          })

          //calculate total to dicount
          auxDiscount = mathOperation(
            price.amount,
            discountOperation,
            'subtraction'
          )
        }
        if (commission) {
          //calculate value before commission
          commissionOperation = mathOperation(
            price.amount,
            (price.amount * commission) / 100,
            'addition',
            2
          )

          commissionTotal.push({
            amount: (price.amount * commission) / 100,
            codeCurrency: price.codeCurrency,
          })
          //calculate total to comission
          auxCommission = mathOperation(
            commissionOperation,
            price.amount,
            'subtraction',
            2
          )
        }

        // operation in total to pay
        if (auxCommission > 0) {
          price.amount = mathOperation(
            price.amount,
            auxCommission,
            'addition',
            2
          )
        }

        if (auxDiscount > 0) {
          price.amount = mathOperation(
            price.amount,
            auxDiscount,
            'subtraction',
            2
          )
        }
      }
    }

    //---> Processing modifiers
    const modifiersFromGrosSubtotal = currentArea?.modifiers?.filter(
      (item) => item.applyToGrossSales && item.active
    )

    for (const modifier of modifiersFromGrosSubtotal ?? []) {
      if (modifier.applyFixedAmount) {
        if (!modifier.fixedPrice) {
          continue
        }

        let normalizedAmount = modifier.fixedPrice.amount
        if (modifier.type === 'discount') {
          normalizedAmount = normalizedAmount * -1
        }

        const found = totalPrices.find(
          (item) => item.codeCurrency === modifier.fixedPrice.codeCurrency
        )

        if (found) {
          totalPrices = totalPrices.map((item) => {
            if (item.codeCurrency === modifier.fixedPrice.codeCurrency) {
              return {
                ...item,
                amount: mathOperation(
                  item.amount,
                  normalizedAmount,
                  'addition',
                  2
                ),
              }
            }

            return item
          })
        } else {
          totalPrices.push({
            amount: normalizedAmount,
            codeCurrency: modifier.fixedPrice.codeCurrency,
          })
        }

        orderModifiers.push({
          showName: modifier.showName || modifier.name,
          amount: normalizedAmount,
          codeCurrency: modifier.fixedPrice.codeCurrency,
        })
      } else {
        for (const price of totalPrices) {
          let normalizedAmount = mathOperation(
            price.amount,
            modifier.amount / 100,
            'multiplication',
            2
          )
          if (modifier.type === 'discount') {
            normalizedAmount = normalizedAmount * -1
          }

          const found = totalPrices.find(
            (item) => item.codeCurrency === price.codeCurrency
          )

          if (found) {
            totalPrices = totalPrices.map((item) => {
              if (item.codeCurrency === price.codeCurrency) {
                return {
                  ...item,
                  amount: mathOperation(
                    item.amount,
                    normalizedAmount,
                    'addition',
                    2
                  ),
                }
              }

              return item
            })
          } else {
            totalPrices.push({
              amount: normalizedAmount,
              codeCurrency: price.codeCurrency,
            })
          }

          orderModifiers.push({
            showName: modifier.showName || modifier.name,
            amount: normalizedAmount,
            codeCurrency: price.codeCurrency,
          })
        }
      }
    }

    //Rest of modifiers by order
    const otherModifiers =
      currentArea?.modifiers
        ?.filter((item) => item.applyAcumulative && item.active)
        .sort((item) => (item.index > item.index ? 1 : -1)) ?? []

    for (const modifier of otherModifiers) {
      if (modifier.applyFixedAmount) {
        if (!modifier.fixedPrice) {
          continue
        }

        let normalizedAmount = modifier.fixedPrice.amount
        if (modifier.type === 'discount') {
          normalizedAmount = normalizedAmount * -1
        }

        const found = totalPrices.find(
          (item) => item.codeCurrency === modifier.fixedPrice.codeCurrency
        )

        if (found) {
          totalPrices = totalPrices.map((item) => {
            if (item.codeCurrency === modifier.fixedPrice.codeCurrency) {
              return {
                ...item,
                amount: mathOperation(
                  item.amount,
                  normalizedAmount,
                  'addition',
                  2
                ),
              }
            }

            return item
          })
        } else {
          totalPrices.push({
            amount: normalizedAmount,
            codeCurrency: modifier.fixedPrice.codeCurrency,
          })
        }

        orderModifiers.push({
          showName: modifier.showName || modifier.name,
          amount: normalizedAmount,
          codeCurrency: modifier.fixedPrice.codeCurrency,
        })
      } else {
        for (const price of totalPrices) {
          let normalizedAmount = mathOperation(
            price.amount,
            modifier.amount / 100,
            'multiplication',
            2
          )
          if (modifier.type === 'discount') {
            normalizedAmount = normalizedAmount * -1
          }

          price.amount = mathOperation(
            price.amount,
            normalizedAmount,
            'addition',
            2
          )

          orderModifiers.push({
            showName: modifier.showName || modifier.name,
            amount: normalizedAmount,
            codeCurrency: price.codeCurrency,
          })
        }
      }
    }

    //5. Adding shipping
    if (shippingPrice) {
      const found = totalPrices?.find(
        (item) => item.codeCurrency === shippingPrice?.codeCurrency
      )

      if (found) {
        totalPrices = totalPrices.map((item) => {
          if (item?.codeCurrency === shippingPrice?.codeCurrency) {
            return {
              ...item,
              amount: mathOperation(
                item?.amount,
                shippingPrice?.amount,
                'addition',
                2
              ),
            }
          }

          return item
        })
      } else {
        totalPrices.push({
          amount: shippingPrice.amount,
          codeCurrency: shippingPrice.codeCurrency,
        })
      }
    }

    //Registering totals to pay
    // const bulkTotal = []
    for (const price of totalPrices) {
      if (!houseCosted) {
        toReturn.push({
          amount: price.amount,
          codeCurrency: price.codeCurrency,
        })
      }
    }
    return {
      toReturn,
      simplePrices,
      aux,
      discountedTotal,
      commissionTotal,
      orderModifiers,
    }
  }, [
    currentArea?.modifiers,
    houseCosted,
    order?.commission,
    order?.discount,
    order?.selledProducts,
    order?.shippingPrice,
    watch,
  ])
  //------------------Calculo de la orden------------

  //Apply selected coupon
  const setCoupon = () => {
    const couponCode = watch('coupon')
    if (!couponCode) {
      toast.error('Seleccione un cupón')
      return
    }
    // const products = order?.selledProducts
    const dataToCoupon: ApplyCouponBody = {
      coupons: [couponCode],
      listProducts:
        order?.selledProducts.map((prod) => ({
          productId: prod.productId,
          quantity: prod.quantity,
          variationId: prod.variationId,
        })) ?? [],
    }
    applyCoupon(dataToCoupon)
  }

  const couponDiscounts = couponResult?.couponDiscount ?? []
  const difference = calculatePaymentDiff(totalWithDiscounts, [
    ...paymentField,
    ...(order?.partialPayments ?? []),
    ...prepaidAmounts,
    ...couponDiscounts,
  ])

  const negativeDiff = difference.some((itm) => itm.amount < 0)

  const totalPayRegister: SimplePrice[] = useMemo(() => {
    if (!order?.partialPayments) return []

    const groupedPayments: Record<string, SimplePrice> = {}

    order.partialPayments.forEach((payment) => {
      const { codeCurrency, amount } = payment

      if (groupedPayments[codeCurrency]) {
        groupedPayments[codeCurrency].amount += amount
      } else {
        groupedPayments[codeCurrency] = { codeCurrency, amount }
      }
    })

    return Object.values(groupedPayments)
  }, [order?.partialPayments])

  //------------------------Table --------------------------//
  const tableTitle = ['No.', 'Fecha', 'Monto', 'Método']
  const tableData: DataTableInterface[] = []
  const [extraTable, setExtraTable] = useState<DataTableInterface[]>([])

  order?.partialPayments?.forEach((item, idx) => {
    tableData.push({
      rowId: idx, //item?.id || ,
      payload: {
        'No.': idx + 1,
        Fecha: moment(item?.createdAt).format('DD/MM'),
        Monto: formatCurrency(item?.amount, item?.codeCurrency),
        Método: translatePaymetMethods(item?.paymentWay),
        amount: item.amount,
        codeCurrency: item.codeCurrency,
        paymentWay: item.paymentWay,
        observations: item.observations,
        id: item.id,
      },
    })
  })

  const addPayToTable = (data: RegisteredPayment) => {
    const pay = data

    setValue('registeredPayments', [...watch('registeredPayments'), pay])
    const lent = watch('registeredPayments').length
    setExtraTable([
      ...extraTable,
      {
        rowId: [...tableData, ...extraTable].length + 1,
        payload: {
          'No.': (order?.partialPayments?.length || 0) + extraTable.length + 1,
          Fecha: moment().format('DD/MM'),
          Monto: formatCurrency(pay?.amount, pay?.codeCurrency),
          Método: translatePaymetMethods(pay?.paymentWay),
          amount: pay?.amount,
          codeCurrency: pay?.codeCurrency,
          paymentWay: pay?.paymentWay,
          observations: pay?.observations,
          madeById: pay?.madeById,
          operationNumber: pay?.operationNumber,
          registeredPaymentsNumber:
            lent === 1 ? 0 : watch('registeredPayments').length - 1,
        },
      },
    ])
  }

  const rowActions = (id: number) => {
    const currentPay = [...tableData, ...extraTable].find(
      (item) => item.rowId === id
    )
    setSelectPay({ ...currentPay?.payload, id } as PaymentTableData['payload'])
  }

  const deletedPay = (id: number) => {
    const currentPay = [...tableData, ...extraTable].find(
      (item) => item.rowId === id
    )
    if (currentPay?.payload.id) {
      //Delete backend
      if (deletePartialPayment) {
        deletePartialPayment(currentPay?.payload.id as number)
      }
    } else {
      const update = extraTable.filter((item) => item.rowId !== id)
      const update2 = update.map((item) => {
        const numberItem = (item?.payload['No.'] || 0) as number

        const newNumber = numberItem > 1 ? numberItem - 1 : numberItem
        return {
          ...item,
          payload: {
            ...item.payload,
            'No.': newNumber,
            registeredPaymentsNumber: newNumber - 1,
          },
        }
      })

      setExtraTable(update2)
      const pays = [...watch('registeredPayments')]

      //:Todo Temporal
      const idx = currentPay?.payload.registeredPaymentsNumber

      pays.splice(idx as number, 1)

      if (pays.length === 0) {
        setValue('registeredPayments', [])
      } else {
        setValue('registeredPayments', [...pays])
      }
    }
  }
  //------------------------Table --------------------------//
  const view = watch('view')
  const RadioValues = [
    {
      label: 'Caja',
      value: 'cash',
    },
    {
      label: 'Pagos anticipados',
      value: 'prepaid',
    },
  ]

  const conditionalWithPay =
    fields.length === 0 &&
    prepaidAmounts.length === 0 &&
    negativeDiff &&
    !houseCosted

  const printOrder = () => {
    printPdf(
      BillingReportPdf({
        order,
        business,
        reportType: 'billing',
      }),
      'Factura'
    )
  }

  if (isFetchingDeleted) {
    return (
      <div className="h-[610px]">
        <Fetching />
      </div>
    )
  }

  return (
    <form>
      <div className="h-full">
        <div className="mt-3">
          {/**Seleccionar Punto de venta y pagos anticipados */}
          <header className="grid grid-cols-2 items-center gap-2">
            {currentArea?.giveChangeWith && (
              <div className="col-span-2 flex w-full items-center gap-x-2">
                <CiWarning className="text-yellow-700" />
                <span className="text-yellow-600">
                  El punto de venta no manejará devoluciones de cambio.
                </span>
              </div>
            )}
            <div className="col-span-2">
              <ComboBox
                control={control}
                data={salesAreas}
                defaultValue={order?.areaSales?.id}
                label="Punto de venta"
                name="areaId"
                rules={{ required: '* Campo requerido' }}
              />
            </div>
          </header>
          <section>
            <div className="mt-4 flex w-full gap-x-3">
              <InlineRadio
                control={control}
                data={RadioValues}
                defaultValue={RadioValues[0]?.value}
                name="view"
              />
            </div>
          </section>

          {/* Formulario de pago */}
          {view === 'cash' && (
            <section className="grid grid-cols-2 rounded-md border border-gray-400">
              {/*Col 1 */}
              <article
                className={cn(
                  'flex min-h-[400px] flex-col justify-between gap-y-2',
                  'overflow-scroll border-r-2 p-2 pt-1 scrollbar-none scrollbar-thumb-slate-100'
                )}
              >
                <section className="flex flex-col justify-between">
                  <header className="my-2 flex items-center justify-between font-semibold">
                    <span className="text-lg">Pagos</span>
                    <div className="max-w-40px max-h-[40px]">
                      <Button
                        action={
                          () => setAddPay(true)
                          // append({
                          //   amount: undefined,
                          //   codeCurrency: business?.costCurrency,
                          //   paymentWay: undefined,
                          // })
                        }
                        color="gray-400"
                        disabled={!areaSelect}
                        icon={<PlusIcon className="h-5" />}
                        outline
                        textColor="slate-600"
                      />
                    </div>
                  </header>
                  <article className=" ">
                    {!addPay && (
                      <>
                        <GenericTable
                          rowAction={rowActions}
                          tableData={[...tableData, ...extraTable]}
                          tableTitles={tableTitle}
                        />
                      </>
                    )}
                    {addPay && (
                      <AddPay
                        action={addPayToTable}
                        close={() => setAddPay(false)}
                        defaultPaymentCurrency={
                          currentArea?.defaultPaymentCurrency
                        }
                        defaultPaymentMethod={currentArea?.defaultPaymentMethod}
                      />
                    )}
                  </article>
                  {/*Pagos parciales */}

                  {/*Pagos parciales */}
                  {/**Pagos actuales */}
                </section>

                {/**Resume ----Toggle---------- */}
                <footer className="my-1 grid grid-cols-1 gap-x-8 border-t border-gray-400">
                  {/* Col 1 */}
                  <div>
                    <div className="col-span-full my-2 flex flex-wrap gap-x-2 gap-y-1">
                      {order?.shipping && !houseCosted && (
                        <div className="w-fit">
                          <Button
                            action={() =>
                              setShippingPriceShow(!shippingPriceShow)
                            }
                            color={`${shippingPriceShow ? 'black' : ''}`}
                            name="Envío"
                            outline={false}
                            textColor={`${
                              shippingPriceShow ? 'white' : 'black'
                            }`}
                          />
                        </div>
                      )}
                      {!houseCosted && (
                        <>
                          {' '}
                          {/**Discount */}
                          {allow_discounts && (
                            <div className="flex flex-col gap-y-3">
                              <Button
                                action={() => setShowDiscount(!showDiscount)}
                                color={`${showDiscount ? 'black' : ''}`}
                                name="Descuento"
                                outline={false}
                                textColor={`${
                                  showDiscount ? 'white' : 'black'
                                }`}
                              />
                            </div>
                          )}
                          {/**Commission */}
                          {allow_commission && (
                            <div className="flex flex-col gap-y-3">
                              <Button
                                action={() =>
                                  setShowCommission(!showCommission)
                                }
                                color={`${showCommission ? 'black' : 'gray'}`}
                                name="Comisión"
                                outline={false}
                                textColor={`${
                                  showCommission ? 'white' : 'black'
                                }`}
                              />
                            </div>
                          )}
                          {show_coupons_in_tablet && (
                            <div>
                              {/* Cupon */}
                              <Button
                                action={() => setShowCupon(!showCupon)}
                                color={`${showCupon ? 'black' : 'gray'}`}
                                name="Cupón"
                                textColor={`${showCupon ? 'white' : 'black'}`}
                              />
                            </div>
                          )}
                        </>
                      )}
                      <div className="">
                        <Button
                          action={() => setHouseCosted(!houseCosted)}
                          color={`${houseCosted ? 'black' : ''}`}
                          name="Consumo casa"
                          outline={false}
                          textColor={`${houseCosted ? 'white' : 'black'}`}
                        />
                      </div>
                    </div>
                    <div className="flex gap-x-2 gap-y-3"></div>
                    {!houseCosted && (
                      <div>
                        {showDiscount && (
                          <div className="col-span-full">
                            <Input
                              control={control}
                              label="Descuento %"
                              name="discount"
                              rules={{
                                min: 0,
                                max: 100,
                              }}
                              type="number"
                            />
                          </div>
                        )}
                        {showCommission && (
                          <div className="col-span-full">
                            <Input
                              control={control}
                              label="Comisión %"
                              name="commission"
                              rules={{
                                min: 0,
                                max: 100,
                              }}
                              type="number"
                            />
                          </div>
                        )}
                      </div>
                    )}

                    <div>
                      {/** Apply Coupon */}
                      {!houseCosted &&
                        !showCommission &&
                        !showDiscount &&
                        showCupon && (
                          <div className="flex max-w-xs gap-2">
                            <AsyncComboBox
                              className="w-full"
                              control={control}
                              dataQuery={{
                                url: '/administration/marketing/coupon',
                              }}
                              disabled={!areaSelect}
                              label="Cupón"
                              name="coupon"
                              normalizeData={{ id: 'code', name: 'code' }}
                            />
                            <div className="pt-6">
                              {couponResult ? (
                                <Button
                                  action={nullCoupon}
                                  color="green-700"
                                  disabled={!areaSelect}
                                  icon={
                                    <X
                                      className="h-5"
                                      onPointerEnterCapture={undefined}
                                      onPointerLeaveCapture={undefined}
                                    />
                                  }
                                  outline
                                  textColor="green-600"
                                />
                              ) : (
                                <Button
                                  action={setCoupon}
                                  color="green-700"
                                  disabled={!areaSelect}
                                  icon={
                                    <Check
                                      className="h-5"
                                      onPointerEnterCapture={undefined}
                                      onPointerLeaveCapture={undefined}
                                    />
                                  }
                                  loading={fetchingCoupon}
                                  outline
                                  textColor="green-600"
                                />
                              )}
                            </div>
                          </div>
                        )}
                    </div>
                  </div>
                  {/**Resume ----Toggle---------- */}
                  <div>
                    {order?.shipping && shippingPriceShow && !houseCosted && (
                      <CurrencyAmountInput
                        byDefault={(() => {
                          if (order?.shippingPrice) {
                            return {
                              amount: order.shippingPrice.amount,
                              codeCurrency: order.shippingPrice.codeCurrency,
                            }
                          }
                          return undefined
                        })()}
                        control={control}
                        currencies={currenciesSelector}
                        defaultCurrency={(() => {
                          if (
                            /*origin === "online_shop" && */ order?.shippingPrice
                          ) {
                            return order.shippingPrice.codeCurrency
                          }
                          return ''
                        })()}
                        label="Costo de envío"
                        name={`shippingPrice`}
                        placeholder="$0.00"
                        // defaultValue={(origin === "online_shop" &&
                        // !!order?.shippingPrice) ? order.shippingPrice.amount! : null}
                        rules={{
                          required:
                            'Escoja el monto y la moneda que desea ingresar',
                          validate: {
                            amountGreaterThanZero: (value) =>
                              value.amount > 0 || 'Monto debe ser mayor a 0',
                            validCurrency: (value) =>
                              value.codeCurrency !== 'Moneda' ||
                              'Escoja una moneda',
                          },
                        }}
                      />
                    )}
                  </div>
                </footer>
              </article>

              {/*Col 2 */}
              <article
                className={`flex flex-col justify-between px-5 gap-y-${
                  !houseCosted ? '10' : '2'
                } mt-3`}
              >
                <aside className="flex flex-col gap-y-3">
                  {/**Subtotal */}
                  <div className="flex justify-between gap-5">
                    <h3 className="text-start font-semibold text-gray-700">
                      Subtotal:
                    </h3>{' '}
                    <span className="flex flex-col">
                      {aux.reduce((total, itm) => total + itm.amount, 0) !==
                      0 ? (
                        aux.map((itm, idx) => (
                          <p className="text-md text-center" key={idx}>
                            {formatCurrency(itm.amount, itm.codeCurrency)}
                          </p>
                        ))
                      ) : (
                        <p className="text-md text-center">0,00</p>
                      )}
                    </span>
                  </div>
                  {/**Costo de envío */}
                  {shippingPrice && (
                    <div className="flex justify-between gap-5">
                      <h3 className="text-start font-semibold text-gray-700">
                        Costo de envío:
                      </h3>{' '}
                      <span className="flex flex-col">
                        {shippingPrice?.amount &&
                        shippingPrice?.amount !== 0 ? (
                          <p className="text-md text-center">
                            {`+${formatCurrency(
                              shippingPrice?.amount,
                              shippingPrice?.codeCurrency
                            )}`}
                          </p>
                        ) : (
                          <p className="text-md text-center">0,00</p>
                        )}
                      </span>
                    </div>
                  )}
                  {/* modificadores */}
                  <div className="">
                    {currentArea?.id === order?.areaSales ? (
                      <span className="flex flex-col">
                        {order?.orderModifiers.reduce(
                          (total, itm) => total + itm.amount,
                          0
                        ) !== 0 &&
                          order?.orderModifiers.map((itm, idx) => (
                            <div
                              className="flex w-full justify-between gap-5"
                              key={idx}
                            >
                              <h3 className="text-center font-semibold text-gray-700">
                                {itm.showName}:
                              </h3>{' '}
                              <p className="text-md text-center" key={idx}>
                                {formatCurrency(itm?.amount, itm?.codeCurrency)}
                              </p>
                            </div>
                          ))}
                      </span>
                    ) : (
                      <span className="flex flex-col">
                        {orderModifiers.reduce(
                          (total, itm) => total + itm.amount,
                          0
                        ) !== 0 &&
                          orderModifiers.map((itm, idx) => (
                            <div
                              className="flex w-full justify-between gap-5"
                              key={idx}
                            >
                              <h3 className="text-center font-semibold text-gray-700">
                                {itm.showName}:
                              </h3>{' '}
                              <p className="text-md text-center" key={idx}>
                                {formatCurrency(itm?.amount, itm?.codeCurrency)}
                              </p>
                            </div>
                          ))}
                      </span>
                    )}
                  </div>
                  {/* Cupones */}
                  {couponDiscounts.length !== 0 && !houseCosted && (
                    <div className="flex justify-between gap-5">
                      <p className="text-start font-semibold text-gray-700">
                        Descuento del cupón:
                      </p>{' '}
                      <span className="flex flex-col">
                        {couponDiscounts.length !== 0 && !houseCosted ? (
                          couponDiscounts.map((itm, idx) => (
                            <p
                              className={`text-md text-center ${
                                itm.amount >= 0
                                  ? 'text-green-500'
                                  : 'text-red-500'
                              }`}
                              key={idx}
                            >
                              {formatCurrency(itm.amount, itm.codeCurrency)}
                            </p>
                          ))
                        ) : (
                          <p className="text-md text-center">0,00</p>
                        )}
                      </span>
                    </div>
                  )}
                  {/* Descuento */}
                  {discountedTotal &&
                    discountedTotal.length !== 0 &&
                    !houseCosted && (
                      <div className="flex justify-between gap-5">
                        <p className="text-start font-semibold text-gray-700">
                          Descuento aplicado:
                        </p>{' '}
                        <span className="flex flex-col">
                          {discountedTotal.length !== 0 && !houseCosted ? (
                            discountedTotal.map(
                              (itm: PriceInvoiceInterface, idx: number) => (
                                <p
                                  className={`text-md text-center ${
                                    itm.amount <= 0
                                      ? 'text-green-500'
                                      : 'text-red-500'
                                  }`}
                                  key={idx}
                                >
                                  -
                                  {formatCurrency(
                                    itm?.amount,
                                    itm?.codeCurrency
                                  )}
                                </p>
                              )
                            )
                          ) : (
                            <></>
                          )}
                        </span>
                      </div>
                    )}
                  {/* Comisiones */}
                  {commissionTotal &&
                    commissionTotal.length !== 0 &&
                    !houseCosted && (
                      <div className="flex justify-between gap-5">
                        <p className="text-start font-semibold text-gray-700">
                          Comisión aplicada:
                        </p>{' '}
                        <span className="flex flex-col">
                          {commissionTotal.length !== 0 && !houseCosted ? (
                            commissionTotal.map(
                              (itm: PriceInvoiceInterface, idx: number) => (
                                <p
                                  className={`text-md text-center ${
                                    itm.amount <= 0
                                      ? 'text-green-500'
                                      : 'text-red-500'
                                  }`}
                                  key={idx}
                                >
                                  +
                                  {formatCurrency(
                                    Math.abs(itm?.amount),
                                    itm?.codeCurrency
                                  )}
                                </p>
                              )
                            )
                          ) : (
                            <></>
                          )}
                        </span>
                      </div>
                    )}

                  {/**Anticipo */}
                  {prepaidAmounts.length !== 0 && (
                    <div className="flex justify-between gap-5">
                      <h3 className="text-center font-semibold text-gray-700">
                        Anticipo:
                      </h3>{' '}
                      <span className="flex flex-col">
                        {prepaidAmounts.length !== 0 ? (
                          prepaidAmounts.map((itm, idx) => (
                            <p className="text-md text-center" key={idx}>
                              {formatCurrency(itm.amount, itm.codeCurrency)}
                            </p>
                          ))
                        ) : (
                          <p className="text-md text-center">0,00</p>
                        )}
                      </span>
                    </div>
                  )}
                </aside>

                <div className="col-span-full mb-3">
                  {/**Total */}
                  <div className="flex flex-col gap-y-3">
                    <div className="flex justify-between gap-5">
                      <h3 className="text-start font-semibold text-gray-700">
                        Total a pagar:
                      </h3>{' '}
                      <span className="flex flex-col">
                        {order?.totalToPay.reduce(
                          (total, itm) => total + itm.amount,
                          0
                        ) !== 0 && !houseCosted ? (
                          totalWithDiscounts.map((itm, idx) => (
                            <p className="text-md text-end" key={idx}>
                              {formatCurrency(itm.amount, itm.codeCurrency)}
                            </p>
                          ))
                        ) : (
                          <p className="text-md text-center">0,00</p>
                        )}
                      </span>
                    </div>

                    {totalPayRegister.length !== 0 && !houseCosted && (
                      <div className="flex justify-between gap-5">
                        <p className="text-start font-semibold text-gray-700">
                          Total Pagado:
                        </p>
                        <span className="flex flex-col">
                          {totalPayRegister.length !== 0 && !houseCosted ? (
                            totalPayRegister.map(
                              (itm: SimplePrice, idx: number) => (
                                <p
                                  className={`text-md ${
                                    itm.amount >= 0
                                      ? 'text-green-500'
                                      : 'text-red-500'
                                  }`}
                                  key={idx}
                                >
                                  {formatCurrency(itm.amount, itm.codeCurrency)}
                                </p>
                              )
                            )
                          ) : (
                            <p className="text-md">0,00</p>
                          )}
                        </span>
                      </div>
                    )}

                    {/**Acumulado de pago  */}
                    {acuPayment?.length > 0 && (
                      <div className="flex justify-between gap-5">
                        <p className="font-semibold text-gray-700">
                          Acumulado de pago:
                        </p>{' '}
                        <span className="flex flex-col">
                          {acuPayment.length !== 0 && !houseCosted ? (
                            acuPayment.map((itm: SimplePrice, idx: number) => (
                              <p
                                className={`text-md text-center ${
                                  itm.amount >= 0
                                    ? 'text-green-500'
                                    : 'text-red-500'
                                }`}
                                key={idx}
                              >
                                {formatCurrency(itm?.amount, itm?.codeCurrency)}
                              </p>
                            ))
                          ) : (
                            <p className="text-md">0,00</p>
                          )}
                        </span>
                      </div>
                    )}
                    {/**Cambio */}
                    {!currentArea?.giveChangeWith && (
                      <div className="flex justify-between gap-5">
                        <p className="font-semibold text-gray-700">Cambio:</p>{' '}
                        <span className="flex flex-col">
                          {difference.length !== 0 && !houseCosted ? (
                            difference.map((itm, idx) => (
                              <p
                                className={`text-md text-center ${
                                  itm.amount >= 0
                                    ? 'text-green-500'
                                    : 'text-red-500'
                                }`}
                                key={idx}
                              >
                                {formatCurrency(itm.amount, itm.codeCurrency)}
                              </p>
                            ))
                          ) : (
                            <p className="text-md">0,00</p>
                          )}
                        </span>
                      </div>
                    )}
                  </div>

                  <footer className="col-span-full my-3 grid w-full grid-cols-2 gap-3 border-t-2 pt-2">
                    <Button
                      action={() => {
                        if (negativeDiff) {
                          printOrder()
                        } else {
                          submitAction(printOrder)
                        }
                      }}
                      color="slate-600"
                      disabled={isFetching}
                      full
                      loading={isFetching && !negativeDiff && loadingHelper}
                      name={`${
                        negativeDiff ? 'Imprimir' : 'Facturar e imprimir'
                      }`}
                      outline
                      textColor="slate-600"
                      //action={() => closeModal()}
                    />

                    <Button
                      action={() => submitAction()}
                      color="slate-600"
                      disabled={isFetching || conditionalWithPay}
                      loading={isFetching && !loadingHelper}
                      name={`${
                        negativeDiff ? 'Registrar pago parcial' : 'Facturar'
                      }`}
                    />
                  </footer>
                </div>
              </article>
            </section>
          )}

          {view === 'prepaid' && <PrepaidList control={control} />}
        </div>
      </div>

      <div className="mt-5 inline-flex w-full justify-between"></div>

      {/* {addPay && (
        <Modal state={addPay} close={setAddPay} size="m">
          <AddPay close={() => setAddPay(false)} action={addPayToTable} />
        </Modal>
      )} */}
      {selectPay && (
        <Modal close={setSelectPay} size="m" state={!!selectPay}>
          <EditPay
            action={() => deletedPay(selectPay.id ?? 0)}
            close={() => setSelectPay(null)}
            data={
              selectPay as unknown as {
                id: string
                amount: number
                codeCurrency: string
                paymentWay: string
                createdAt: string
                operationNumber?: string
                observations?: string
              }
            }
          />
        </Modal>
      )}
    </form>
  )
}

export default PaymentContainerV2
